import type { User } from "firebase/auth";
import type { AccountProfile, AdminMetrics, ReleaseInfo, Review } from "../types";

async function request<T>(path: string, user?: User | null, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (user) headers.set("Authorization", `Bearer ${await user.getIdToken()}`);

  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  if (!response.ok) {
    let message = "No pudimos completar la solicitud.";
    try {
      const payload = (await response.json()) as { message?: string };
      if (payload.message) message = payload.message;
    } catch {
      // The friendly generic message is safer than exposing an upstream response.
    }
    throw new Error(`THOT:${message}`);
  }
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    throw new Error("THOT:El servidor todavía no está conectado. Probá de nuevo en unos minutos.");
  }
  return response.json() as Promise<T>;
}

export const api = {
  bootstrap: (user: User, profile: { firstName?: string; lastName?: string }) =>
    request<AccountProfile>("/api/account/bootstrap", user, {
      method: "POST",
      body: JSON.stringify(profile),
    }),
  me: (user: User) => request<AccountProfile>("/api/me", user),
  releases: () => request<{ releases: ReleaseInfo[] }>("/api/releases"),
  reviews: (user: User) => request<{ reviews: Review[] }>("/api/reviews", user),
  createReview: (user: User, rating: number, comment: string) =>
    request<{ review: Review }>("/api/reviews", user, {
      method: "POST",
      body: JSON.stringify({ rating, comment }),
    }),
  adminMetrics: (user: User) => request<AdminMetrics>("/api/admin/metrics", user),
  moderateReview: (user: User, id: string, status: "published" | "hidden") =>
    request<{ review: Review }>(`/api/admin/reviews/${encodeURIComponent(id)}`, user, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
};

export async function downloadRelease(user: User, release: ReleaseInfo): Promise<void> {
  if (!release.version) throw new Error("THOT:Todavía no publicamos esta versión.");
  const token = await user.getIdToken();
  const response = await fetch(
    `/api/download/${encodeURIComponent(release.target)}/${encodeURIComponent(release.version)}`,
    { headers: { Authorization: `Bearer ${token}` }, credentials: "same-origin" },
  );
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(`THOT:${payload.message ?? "No pudimos preparar la descarga."}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? `THOT-GC-${release.version}.zip`;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Chrome may still be consuming the blob after click(). Revoking the URL
  // immediately can leave the download stuck as a .crdownload file.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
